// SG-REBUILD-PORTFOLIO-COORDINATION-20261012-001 (founder 13:36 tracking scope) — Meta Pixel and LinkedIn Insight,
// behind the visitor's EXISTING advertising choice (@/lib/consent, cookie `sg_consent_advertising`).
//
// THE RAILS:
//   1. NOTHING BEFORE AN EXPLICIT GRANT. No script, no queue, no cookie until `getStoredConsent() === "granted"`. No
//      choice and "denied" are the same state here. Every send re-reads the cookie, so a stale in-memory state can
//      never outlive the choice.
//   2. STOPS ON REVOCATION. A later "denied" revokes Meta (`fbq("consent", "revoke")` — Meta's supported switch),
//      silences both command queues, removes the tags and clears the first-party ad cookies. LinkedIn has no
//      supported revoke call, so a page that actually loaded LinkedIn's script is reloaded once; the reload boots
//      with "denied" and loads nothing.
//   3. PRODUCTION HOSTS ONLY (`isLiveHost`, the same gate GA4 uses). Staging, preview and localhost NEVER load a
//      vendor script or reach a vendor endpoint; with advertising granted they record the commands they WOULD have
//      sent in `window.__sgAdPixelsDryRun`, which is how staging proves the wiring without a production send.
//   4. NO PERSONAL DATA. No advanced matching, no user data, no form scraping (`autoConfig` off). Events carry
//      product/leg ids, a title, a quantity and a CAD value — the same fields GA4 already receives.
//   5. GENUINE SEAMS ONLY. ViewContent, InitiateCheckout and Purchase are mirrored from @/lib/analytics-event's
//      trackViewItem / trackBeginCheckout and @/lib/analytics-purchase's emitPurchaseOnce, so Purchase keeps that
//      module's confirmed-order and not-a-preview rails. Nothing here invents an event.
import { isLiveHost } from "@/lib/analytics-init";
import { getStoredConsent } from "@/lib/consent";

export interface AdPixelConfig {
  /** Meta Pixel (dataset) id — founder 13:36. Public: it is printed in every page that loads the pixel. */
  metaPixelId: string | null;
  /** LinkedIn Insight Tag partner id. `null` until the founder's value is resolved — never guessed. */
  linkedinPartnerId: string | null;
  /** LinkedIn conversion rule ids (Campaign Manager), per event. Empty until created there. */
  linkedinConversionIds: Partial<Record<AdConversion, number>>;
}

export type AdConversion = "ViewContent" | "InitiateCheckout" | "Purchase";

// THE ONE PLACE TO CONFIGURE THE TAGS. A source constant on purpose, like GA_ID in @/lib/analytics-init: a public tag
// id gated by host needs no build variable, and a NEXT_PUBLIC_* value would need turbo.json + Dockerfile + CI build
// args or it silently builds as undefined.
export const AD_PIXEL_CONFIG: AdPixelConfig = {
  metaPixelId: "1487672352654474",
  linkedinPartnerId: null, // PENDING founder value. Set the numeric partner id here; nothing loads until then.
  linkedinConversionIds: {},
};

const META_SRC = "https://connect.facebook.net/en_US/fbevents.js";
const LINKEDIN_SRC = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
const TAG_ATTR = "data-sg-ad-pixel";
const DIGITS = /^\d{5,20}$/;
/** First-party cookies the two tags write on our domain. */
const AD_COOKIES = ["_fbp", "_fbc", "li_fat_id"];
const CURRENCY = "CAD";

export type AdPixelMode = "live" | "dry-run";
export type AdSendOutcome = "sent" | "dry-run" | "no-window" | "not-granted" | "inactive" | "duplicate" | "empty";

export interface DryRunEntry {
  vendor: "meta" | "linkedin";
  args: unknown[];
}

type Fbq = ((...args: unknown[]) => void) & { queue?: unknown[]; loaded?: boolean; version?: string; disablePushState?: boolean; push?: unknown; callMethod?: (...a: unknown[]) => void };
type Lintrk = ((...args: unknown[]) => void) & { q?: unknown[] };

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
    lintrk?: Lintrk;
    _linkedin_partner_id?: string;
    _linkedin_data_partner_ids?: string[];
    __sgAdPixelsDryRun?: DryRunEntry[];
  }
}

interface State {
  config: AdPixelConfig;
  active: boolean;
  mode: AdPixelMode | null;
  meta: boolean;
  linkedin: boolean;
  linkedinScriptLoaded: boolean;
  pageViewSent: boolean;
}

const fresh = (): State => ({ config: AD_PIXEL_CONFIG, active: false, mode: null, meta: false, linkedin: false, linkedinScriptLoaded: false, pageViewSent: false });
let state: State = fresh();
const viewedThisLoad = new Set<string>();
const checkoutsThisLoad = new Set<string>();
const purchasesThisLoad = new Set<string>();

const granted = () => getStoredConsent() === "granted";
const validId = (v: string | null | undefined): v is string => typeof v === "string" && DIGITS.test(v);

export function adPixelMode(): AdPixelMode | null {
  if (typeof window === "undefined") return null;
  return isLiveHost(window.location.hostname) ? "live" : "dry-run";
}

function appendScript(src: string): void {
  const s = document.createElement("script");
  s.async = true;
  s.src = src;
  s.setAttribute?.(TAG_ATTR, "");
  document.body.appendChild(s);
}

// The real SDK function for this document, kept across a revoke. Revocation replaces `window.fbq` with a no-op, so a
// later re-grant must restore THIS function (which fbevents.js drains or has wired through callMethod) rather than
// mistake the no-op for the SDK. Inits are per document: the SDK is told about a pixel id once.
let metaSdk: Fbq | null = null;
const metaInitialised = new Set<string>();
let linkedinSdk: Lintrk | null = null;

function loadMeta(id: string): void {
  if (metaSdk) {
    window.fbq = window._fbq = metaSdk;
  } else if (!window.fbq) {
    // Meta's base snippet, typed: a queue that fbevents.js drains once it arrives.
    const queue: unknown[] = [];
    const fbq: Fbq = Object.assign(
      (...args: unknown[]) => {
        if (fbq.callMethod) fbq.callMethod(...args);
        else queue.push(args);
      },
      { loaded: true, version: "2.0", queue },
    );
    fbq.push = fbq;
    // One PageView per document, sent below — the same model GA4 uses (no route-change page views). Meta's own
    // history listener would otherwise add page views this module does not control.
    fbq.disablePushState = true;
    window.fbq = fbq;
    window._fbq = window._fbq ?? fbq;
    appendScript(META_SRC);
  }
  const sdk = window.fbq;
  if (!sdk) return;
  metaSdk = sdk;
  sdk("consent", "grant");
  if (metaInitialised.has(id)) return;
  metaInitialised.add(id);
  // No automatic events or automatic configuration (they read on-page buttons and form fields).
  sdk("set", "autoConfig", false, id);
  sdk("init", id);
}

function loadLinkedIn(id: string): void {
  window._linkedin_partner_id = id;
  window._linkedin_data_partner_ids = window._linkedin_data_partner_ids ?? [];
  if (!window._linkedin_data_partner_ids.includes(id)) window._linkedin_data_partner_ids.push(id);
  if (linkedinSdk) {
    window.lintrk = linkedinSdk;
  } else if (!window.lintrk) {
    const q: unknown[] = [];
    window.lintrk = Object.assign((...args: unknown[]) => void q.push(args), { q });
  }
  linkedinSdk = window.lintrk ?? null;
  // The Insight Tag records the page view itself when it loads; there is no separate PageView command.
  appendScript(LINKEDIN_SRC);
  state.linkedinScriptLoaded = true;
}

function dryRun(vendor: DryRunEntry["vendor"], ...args: unknown[]): void {
  (window.__sgAdPixelsDryRun = window.__sgAdPixelsDryRun ?? []).push({ vendor, args });
}

function activate(config: AdPixelConfig, mode: AdPixelMode): void {
  state.config = config;
  state.active = true;
  state.mode = mode;
  const metaId = validId(config.metaPixelId) ? config.metaPixelId : null;
  const linkedinId = validId(config.linkedinPartnerId) ? config.linkedinPartnerId : null;
  state.meta = metaId !== null;
  state.linkedin = linkedinId !== null;
  if (mode === "live") {
    if (metaId) loadMeta(metaId);
    if (linkedinId) loadLinkedIn(linkedinId);
  } else {
    if (metaId) dryRun("meta", "init", metaId);
    if (linkedinId) dryRun("linkedin", "init", linkedinId);
  }
  if (!state.pageViewSent) {
    state.pageViewSent = true;
    if (state.meta) command("meta", "track", "PageView");
    if (state.linkedin && mode === "dry-run") dryRun("linkedin", "PageView (automatic on tag load)");
  }
}

function clearAdCookies(): void {
  const host = window.location.hostname;
  const parts = host.split(".");
  const domains = ["", host, ...parts.slice(1).map((_, i) => "." + parts.slice(i + 1).join("."))].filter((d) => d !== ".");
  for (const name of AD_COOKIES) {
    for (const d of new Set(domains)) {
      document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT${d ? `; domain=${d}` : ""}`;
    }
  }
}

function deactivate(): void {
  const { mode, meta, linkedinScriptLoaded } = state;
  if (mode === "live") {
    if (meta) {
      try {
        window.fbq?.("consent", "revoke");
      } catch {
        /* a replaced fbq cannot block the rest of the teardown */
      }
    }
    const noop = (() => undefined) as Fbq & Lintrk;
    if (window.fbq) window.fbq = window._fbq = noop;
    if (window.lintrk) window.lintrk = noop;
    delete window._linkedin_partner_id;
    delete window._linkedin_data_partner_ids;
    document.querySelectorAll?.(`script[${TAG_ATTR}]`).forEach((s) => s.remove());
    clearAdCookies();
  } else if (mode === "dry-run") {
    if (meta) dryRun("meta", "consent", "revoke");
    if (state.linkedin) dryRun("linkedin", "revoke (page reload on a live host)");
  }
  // Keep pageViewSent: a re-grant later in the same document is not a second page view.
  state = { ...fresh(), pageViewSent: state.pageViewSent };
  if (mode === "live" && linkedinScriptLoaded) {
    // LinkedIn's script cannot be told to stop; a fresh document with "denied" stored never loads it.
    window.location.reload();
  }
}

/**
 * Bring the tags in line with the stored advertising choice. Called on mount, after every fresh banner choice and
 * when the tab becomes visible again (a choice changed in another tab). Idempotent.
 */
export function syncAdPixels(config: AdPixelConfig = AD_PIXEL_CONFIG): "active" | "inactive" | "no-window" {
  const mode = adPixelMode();
  if (!mode) return "no-window";
  if (granted()) {
    if (!state.active) activate(config, mode);
    return "active";
  }
  if (state.active) deactivate();
  return "inactive";
}

function command(vendor: "meta" | "linkedin", ...args: unknown[]): void {
  if (state.mode === "dry-run") return dryRun(vendor, ...args);
  try {
    if (vendor === "meta") window.fbq?.(...args);
    else window.lintrk?.(...args);
  } catch {
    /* measurement never breaks a page */
  }
}

/** The send gate: a grant on record now (re-read, never cached) and tags active in this document. */
function gate(): AdSendOutcome | null {
  if (typeof window === "undefined") return "no-window";
  if (!granted()) {
    if (state.active) deactivate();
    return "not-granted";
  }
  if (!state.active) return "inactive";
  return null;
}

function send(event: AdConversion, params: Record<string, unknown>, eventId?: string): AdSendOutcome {
  if (state.meta) command("meta", "track", event, params, ...(eventId ? [{ eventID: eventId }] : []));
  const conversionId = state.config.linkedinConversionIds[event];
  if (state.linkedin && typeof conversionId === "number") {
    command("linkedin", "track", { conversion_id: conversionId, ...(eventId ? { event_id: eventId } : {}) });
  }
  return state.mode === "dry-run" ? "dry-run" : "sent";
}

export interface AdItem {
  item_id: string;
  item_name?: string;
  item_category?: string;
  quantity: number;
  price: number;
}

const value = (items: AdItem[]) => Math.round(items.reduce((sum, i) => sum + i.quantity * i.price, 0) * 100) / 100;
const numItems = (items: AdItem[]) => items.reduce((sum, i) => sum + i.quantity, 0);

/** ViewContent — a product/ride detail the visitor opened (mirrors GA4 view_item). Once per item per page load. */
export function adViewContent(item: AdItem): AdSendOutcome {
  const closed = gate();
  if (closed) return closed;
  if (viewedThisLoad.has(item.item_id)) return "duplicate";
  viewedThisLoad.add(item.item_id);
  return send("ViewContent", {
    content_ids: [item.item_id],
    content_type: "product",
    ...(item.item_name ? { content_name: item.item_name } : {}),
    ...(item.item_category ? { content_category: item.item_category } : {}),
    value: value([item]),
    currency: CURRENCY,
  });
}

const CHECKOUT_KEY = "sg.ads.initiate_checkout";

/** InitiateCheckout — the pay rail opened with a real cart (mirrors GA4 begin_checkout). Once per cart content per
 *  browser session, like begin_checkout; a changed cart is a new attempt. */
export function adInitiateCheckout(items: AdItem[]): AdSendOutcome {
  if (items.length === 0) return "empty";
  const closed = gate();
  if (closed) return closed;
  const fingerprint = items.map((i) => `${i.item_id}x${i.quantity}`).sort().join("|");
  let stored: string | null = null;
  try {
    stored = window.sessionStorage.getItem(CHECKOUT_KEY);
  } catch {
    /* storage blocked: the in-memory set still covers this page load */
  }
  if (checkoutsThisLoad.has(fingerprint) || stored === fingerprint) return "duplicate";
  checkoutsThisLoad.add(fingerprint);
  try {
    window.sessionStorage.setItem(CHECKOUT_KEY, fingerprint);
  } catch {
    /* see above */
  }
  return send("InitiateCheckout", { content_ids: items.map((i) => i.item_id), content_type: "product", num_items: numItems(items), value: value(items), currency: CURRENCY });
}

const PURCHASE_KEY = "sg.ads.purchases";
const PURCHASE_RETENTION_MS = 30 * 24 * 60 * 60 * 1000;
const PURCHASE_MAX = 50;

interface PurchaseRow {
  id: string;
  at: number;
}

function purchaseLedger(): PurchaseRow[] {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(PURCHASE_KEY) ?? "[]") as unknown;
    if (!Array.isArray(parsed)) return [];
    return (parsed as unknown[]).filter(
      (r): r is PurchaseRow => !!r && typeof (r as PurchaseRow).id === "string" && typeof (r as PurchaseRow).at === "number",
    );
  } catch {
    return [];
  }
}

/**
 * Purchase — ONLY from @/lib/analytics-purchase's emitPurchaseOnce, after its confirmed-order and not-a-preview rails.
 * Once per order: in memory and in a bounded localStorage ledger (same 50-order / 30-day window as GA4's). The order id
 * is the dedup key and Meta's `eventID`; it is never put in the event's parameters.
 */
export function adPurchase(orderId: string, amount: number, items: AdItem[], currency = CURRENCY): AdSendOutcome {
  const closed = gate();
  if (closed) return closed;
  const cutoff = Date.now() - PURCHASE_RETENTION_MS;
  if (purchasesThisLoad.has(orderId) || purchaseLedger().some((r) => r.id === orderId && r.at > cutoff)) return "duplicate";
  purchasesThisLoad.add(orderId);
  try {
    const rows = purchaseLedger().filter((r) => r.id !== orderId && r.at > cutoff).concat({ id: orderId, at: Date.now() }).slice(-PURCHASE_MAX);
    window.localStorage.setItem(PURCHASE_KEY, JSON.stringify(rows));
  } catch {
    /* the in-memory set still covers this page load */
  }
  return send(
    "Purchase",
    { content_ids: items.map((i) => i.item_id), content_type: "product", num_items: numItems(items), value: Number.isFinite(amount) ? amount : 0, currency },
    `purchase.${orderId}`,
  );
}

/** Test-only: module state is per page load. */
export function _resetAdPixelsForTests(): void {
  state = fresh();
  metaSdk = null;
  metaInitialised.clear();
  linkedinSdk = null;
  viewedThisLoad.clear();
  checkoutsThisLoad.clear();
  purchasesThisLoad.clear();
}
